import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import moment from 'moment';

import { modelCalendarParseEventsTime } from '@libs/Calendar';
import { eventPropGetter } from '@libs/Calendar';
import { isJobEvent } from '@libs/EventTypes';
import { getMoment } from '@libs/NwMoment';
import { saxonize } from '@libs/NwUtils';

import { UserContext } from '@contexts/UserContext';
import { useUserGuiConfig } from '@contexts/UserGuiConfigContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import { Loading } from '@components/Gui/Loading';
import NwDrawer from '@components/Gui/NwDrawer';
import DailyCalendarWrapper from '@components/Schedule/Calendar/CalendarUI-DailyDrawer';
import EventCard from '@components/Schedule/Calendar/EventCard';
import { getScheduleEvents } from '@components/Schedule/ScheduleUtils';

const GeneralBigCalendar = Calendar;

moment.tz.setDefault('UTC');
const localizer = momentLocalizer(moment);
localizer.segmentOffset = 0;

const convertEvent = (event) => {
    let newEvent = { ...event };

    if (event.grouped) {
        if (event.grouped.find((model) => model.Acknowledgment === 'NotChecked')) {
            newEvent.Acknowledgment = 'NotChecked';
        } else {
            newEvent.Acknowledgment = 'Checked';
        }

        if (event.grouped.find((model) => !model.Cancel)) {
            newEvent.Cancel = false;
        } else {
            newEvent.Cancel = true;
        }
    }
    return newEvent;
};

const DailyCalendarDrawer = ({ params, filters, scheduleRange, onClose }) => {
    const [isFetching, setIsFetching] = useState(false);
    const userGuiConfig = useUserGuiConfig();
    const [events, setEvents] = useState([]);
    const { currentUser } = useContext(UserContext);

    moment.locale('us', {
        week: {
            dow: 1,
            doy: 1,
        },
    });

    useEffect(() => {
        if (scheduleRange) {
            fetchEvents();
        }
    }, [params, scheduleRange]);

    const fetchEvents = async () => {
        setIsFetching(true);
        const scheduleEvents = await getScheduleEvents(params, scheduleRange);
        setEvents(modelCalendarParseEventsTime(scheduleEvents));
        setIsFetching(false);
    };

    const displayedEvents = useMemo(() => {
        if (events) {
            let filteredEvents = [...events];
            filteredEvents = filteredEvents.map((event) => convertEvent(event));

            if (filters.filterAcknowledgement === 'checked') {
                filteredEvents = filteredEvents.filter((event) => event.Acknowledgment === 'Checked');
            } else if (filters.filterAcknowledgement === 'tocheck') {
                filteredEvents = filteredEvents.filter((event) => event.Acknowledgment === 'NotChecked');
            }
            if (filters.filterMine === 'mine') {
                filteredEvents = filteredEvents.filter(
                    (event) =>
                        event.UserCreated === currentUser.UserID ||
                        (event.Project && event.Project.UserCreated === currentUser.UserID)
                );
            }

            if (filters.hideCancelled) {
                filteredEvents = filteredEvents.filter((event) => !event.Cancel);
            }
            if (filters.hideBilling) {
                filteredEvents = filteredEvents.filter(
                    (event) => !event.Locked && isJobEvent(event.TypeName) && event.Confirmed > 0
                );
            }
            return filteredEvents;
        }
        return [];
    }, [events, scheduleRange, filters]);

    const getEditDrawerTitle = () => {
        let title = getMoment(scheduleRange.start).format('ddd Do MMM YYYY');
        if (params.General) {
            title += ' - Agency Calendar';
        } else {
            if (params.idmodels && params.idmodels.length === 1) {
                title += ` - ${saxonize(getContactName(params.idmodels[0]))} Calendar`;
            }
        }
        return title;
    };

    const scrollRef = useRef(null);

    const scrollToBottom = () => {
        if (scrollRef && scrollRef.current) {
            const timeHeaderDiv = scrollRef.current.querySelector('.rbc-time-header');
            const timeHeaderDivHeight = timeHeaderDiv.offsetHeight;
            const timeColumn = scrollRef.current.querySelector('.rbc-time-column');
            const timeSlotToScroll = timeColumn.querySelectorAll('.rbc-timeslot-group')[5];
            const timeSlotToScrollHeight = timeSlotToScroll.offsetHeight;
            if (timeSlotToScroll) {
                timeSlotToScroll.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    useEffect(() => {
        setTimeout(() => {
            scrollToBottom();
        }, 1000);
    }, []);

    return (
        <NwDrawer onClose={onClose} title={getEditDrawerTitle()} width="80vw" padding="0">
            {isFetching ? (
                <Loading textBlack paddingTop="5rem" />
            ) : (
                <DailyCalendarWrapper>
                    <div className="general-calendar agenda-view">
                        <div className="calendar" ref={scrollRef}>
                            <GeneralBigCalendar
                                formats={{
                                    timeGutterFormat: userGuiConfig.calendar.timeFormat === '24' ? 'H:mm' : 'h:mm a',
                                }}
                                tooltipAccessor={null}
                                selectable="ignoreEvents"
                                localizer={localizer}
                                defaultDate={getMoment(scheduleRange.start).toDate()}
                                views={{ day: true }}
                                agendaView
                                components={{
                                    event: EventCard,
                                    toolbar: (toolbar) => <div />,
                                }}
                                defaultView={Views.DAY}
                                eventPropGetter={eventPropGetter}
                                events={displayedEvents}
                            />
                        </div>
                    </div>
                </DailyCalendarWrapper>
            )}
        </NwDrawer>
    );
};

export default DailyCalendarDrawer;
